import { supabase } from '../lib/supabase'
import { fetchAllByRange } from '../lib/supabaseFetchAll'
import type {
  CctBuscarDadosCategoria,
  CctBuscarDadosConvencao,
  CctBuscarDadosFilters,
  CctBuscarDadosParameter,
  CctBuscarDadosResultado,
  CctBuscarDadosWebhookPayload,
} from '../types/cctBuscarDados'

const CONVENCOES_COLUMNS =
  'id, created_at, cnpj_entidade_laboral, cnpj_entidade_patronal, id_scgp, status, status_mte, cod_categoria, desc_territorial, num_vidas, convenios_qt, data_inicio_vigencia, data_fim_vigencia'

function parseWebhookResponse(text: string): Record<string, unknown> {
  const trimmed = text.trim()
  if (!trimmed) return { raw: '' }
  try {
    const parsed = JSON.parse(trimmed) as unknown
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }
    return { data: parsed as unknown }
  } catch {
    return { raw: trimmed }
  }
}

export const cctBuscarDadosService = {
  async listParameters(): Promise<CctBuscarDadosParameter[]> {
    const { data, error } = await supabase
      .from('cct_buscar_dados_parameters')
      .select('*')
      .order('key', { ascending: true })

    if (error) throw error
    // prompt_ia foi descontinuado — oculta se ainda existir no banco
    return ((data || []) as CctBuscarDadosParameter[]).filter((p) => p.key !== 'prompt_ia')
  },

  async updateParameter(
    id: string,
    data: { value: string; description?: string },
  ): Promise<CctBuscarDadosParameter> {
    const payload: { value: string; description?: string; updated_at: string } = {
      value: data.value,
      updated_at: new Date().toISOString(),
    }
    if (data.description !== undefined) payload.description = data.description

    const { data: result, error } = await supabase
      .from('cct_buscar_dados_parameters')
      .update(payload)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return result as CctBuscarDadosParameter
  },

  getParamValue(parameters: CctBuscarDadosParameter[], key: string, fallback = ''): string {
    return parameters.find((p) => p.key === key)?.value ?? fallback
  },

  async listCategorias(): Promise<CctBuscarDadosCategoria[]> {
    const res = await fetchAllByRange<CctBuscarDadosCategoria>((from, to) =>
      supabase
        .from('cct_categorias')
        .select('id, nome')
        .order('nome', { ascending: true })
        .range(from, to),
    )
    if (res.error) throw new Error(res.error)
    return res.data
  },

  async listConvencoes(filters?: CctBuscarDadosFilters): Promise<CctBuscarDadosConvencao[]> {
    const res = await fetchAllByRange<CctBuscarDadosConvencao>((from, to) => {
      let query = supabase
        .from('cct_convencoes')
        .select(CONVENCOES_COLUMNS)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })

      if (filters?.idScgps?.length) {
        const ids = filters.idScgps
          .map((v) => Number(v))
          .filter((n) => Number.isFinite(n))
        if (ids.length) query = query.in('id_scgp', ids)
      }
      if (filters?.cnpjsLaborais?.length) {
        query = query.in('cnpj_entidade_laboral', filters.cnpjsLaborais)
      }
      if (filters?.cnpjsPatronais?.length) {
        query = query.in('cnpj_entidade_patronal', filters.cnpjsPatronais)
      }
      if (filters?.codCategorias?.length) {
        query = query.in('cod_categoria', filters.codCategorias)
      }

      return query.range(from, to)
    })

    if (res.error) throw new Error(res.error)
    return res.data.map((row) => ({
      ...row,
      id_scgp: row.id_scgp != null ? Number(row.id_scgp) : null,
      num_vidas: row.num_vidas != null ? Number(row.num_vidas) : null,
      convenios_qt: row.convenios_qt != null ? Number(row.convenios_qt) : null,
    }))
  },

  /** Carrega todas as convenções (para montar opções dos filtros). */
  async listAllConvencoesForFilters(): Promise<CctBuscarDadosConvencao[]> {
    return this.listConvencoes()
  },

  async listResultados(limit = 1000): Promise<CctBuscarDadosResultado[]> {
    const res = await fetchAllByRange<CctBuscarDadosResultado>((from, to) =>
      supabase
        .from('cct_buscar_dados_resultados')
        .select('*')
        .order('created_at', { ascending: false })
        .range(from, to),
    )
    if (res.error) throw new Error(res.error)

    const rows = res.data.map((row) => ({
      ...row,
      id_scgp: Number(row.id_scgp),
      convencao_ids: Array.isArray(row.convencao_ids)
        ? row.convencao_ids.map((n: unknown) => Number(n))
        : null,
    })) as CctBuscarDadosResultado[]

    return limit > 0 ? rows.slice(0, limit) : rows
  },

  /** Distinct id_scgp que já constam em cct_buscar_dados_resultados (não reprocessar na Busca). */
  async listIdScgpsJaProcessados(): Promise<Set<number>> {
    const res = await fetchAllByRange<{ id_scgp: number }>((from, to) =>
      supabase
        .from('cct_buscar_dados_resultados')
        .select('id_scgp')
        .order('created_at', { ascending: false })
        .range(from, to),
    )
    if (res.error) throw new Error(res.error)

    const set = new Set<number>()
    for (const row of res.data) {
      const id = Number(row.id_scgp)
      if (Number.isFinite(id)) set.add(id)
    }
    return set
  },

  /** @deprecated Use listIdScgpsJaProcessados */
  async listIdScgpsProcessadosComSucesso(): Promise<Set<number>> {
    return this.listIdScgpsJaProcessados()
  },

  /**
   * Envia o documento já extraído do zip (não o .zip) ao webhook n8n.
   * A gravação em cct_buscar_dados_resultados fica a cargo do n8n.
   */
  async sendDocumentoToWebhook(
    webhookUrl: string,
    payload: CctBuscarDadosWebhookPayload,
  ): Promise<Record<string, unknown>> {
    const url = webhookUrl.trim()
    if (!url) {
      throw new Error('URL do webhook n8n não configurada. Preencha o parâmetro webhook_n8n.')
    }

    const form = new FormData()
    form.append('file', payload.file, payload.fileName)
    form.append('id_scgp', String(payload.idScgp))
    form.append('arquivo', payload.fileName)

    const response = await fetch(url, {
      method: 'POST',
      body: form,
    })

    const text = await response.text()
    if (!response.ok) {
      throw new Error(
        `Webhook retornou ${response.status}: ${text.slice(0, 500) || response.statusText}`,
      )
    }

    return parseWebhookResponse(text)
  },

  /** @deprecated Use sendDocumentoToWebhook */
  async sendZipToWebhook(
    webhookUrl: string,
    payload: CctBuscarDadosWebhookPayload,
  ): Promise<Record<string, unknown>> {
    return this.sendDocumentoToWebhook(webhookUrl, payload)
  },
}
